import React, { Component } from 'react'
import { Trans, withI18n, withI18nProps } from '@lingui/react'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'
import compose from 'lodash/fp/compose'

import withMarketing, {
  MarketingProps,
} from 'client/shared/decorators/with-marketing'
import prepareComponent from 'client/shared/decorators/prepare-component'
import errorBoundary, {
  ErrorBoundaryProps,
} from 'client/shared/decorators/error-boundary'
import urlFor, { QueryParams } from 'shared/tools/url-helper'
import deviceHelper from 'shared/tools/device-helper'

import { LayoutColumn } from 'client/shared/blocks/layout-column'
import { ShelfInfo } from 'client/bookmate/blocks/shelf-info'
import { ShelfAnnotation } from 'client/bookmate/blocks/shelf-annotation'
import MoreShelves from 'client/bookmate/blocks/more-shelves'
import Showcase from 'client/bookmate/blocks/showcase'
import UsersList from 'client/bookmate/blocks/users-list'
import TopicList from 'client/bookmate/blocks/topic-list'
import Topic from 'client/bookmate/blocks/topic'
import List from 'client/bookmate/blocks/list'
import { MoreButtonWithLoading } from 'client/shared/blocks/more-button'
import Footer from 'client/shared/blocks/footer'
import Button from 'client/shared/blocks/button'
import FollowButtonBox, {
  DK_SHELF_UUID,
  DK_SUMMER_SHELF_UUID,
} from 'client/bookmate/boxes/follow-button-box'
import { ShelfContextMenuBox } from 'client/shared/boxes/shelf-context-menu-box'
import ShelfDeeplinkButton from 'client/shared/blocks/shelf-deeplink-button'
import Spacer from 'client/shared/blocks/spacer'
import Meta from 'client/shared/blocks/meta'
import { PromoGifts } from 'client/bookmate/blocks/promo-gifts'
import { ShareButton } from 'client/shared/blocks/share-button'
import ButtonsRow from 'client/shared/blocks/buttons-row'

import HeaderBox from 'client/shared/boxes/header-box'
import ShelfSubHeaderBox from 'client/bookmate/boxes/shelf-sub-header-box'
import { CardPostBox } from 'client/bookmate/boxes/card-post-box'

import {
  getShelfById,
  getShelvesByIds,
} from 'client/bookmate/reducers/shelves-reducer'
import { getUsersByIds } from 'client/bookmate/reducers/users-reducer'
import { getPosts } from 'client/bookmate/reducers/posts-reducer'
import {
  loadPosts,
  loadFollowers,
} from 'client/bookmate/reducers/shelf-reducer'
import { loadShelves } from 'client/bookmate/reducers/user-reducer'

import { Dispatch, State } from 'shared/types/redux'
import { Params } from 'client/shared/types/react-router'
import { PostProps } from 'client/shared/types/post'
import { State as AppState } from 'client/shared/reducers/app-reducer'
import { FullShelfProps, ShelfProps } from 'client/shared/types/shelf'
import { UserShort } from 'client/shared/types/user'
import { Locale } from 'client/shared/types/current-user'
import { isShareAvailable } from 'client/shared/helpers/socials-helpers'

type Props = {
  userShelves: ShelfProps[]
  page: string
  isLoggedIn?: boolean
  dispatch: Dispatch
  app: AppState
  params: Params
  shelfPosts: PostProps[]
  postsPage: number
  locale: Locale
  shelf: FullShelfProps
  shelfFollowers: UserShort[]
  query?: QueryParams
} & ErrorBoundaryProps &
  withI18nProps &
  MarketingProps

class ShelfIndexBox extends Component<Props> {
  static defaultProps = {
    shelfFollowers: [],
  }

  getShareData() {
    const {
      i18n,
      locale,
      shelf: { uuid, title },
      app: { domain, query },
    } = this.props

    const url = urlFor.shelfWithDomain(uuid, locale, domain, query)

    return {
      facebook: { url },
      twitter: {
        url,
        title: i18n._('shelf.twitter_share', { title }),
      },
    }
  }

  componentDidMount() {
    const { dispatch, shelf } = this.props

    dispatch(loadShelves(shelf.creator.login, 1, 4))
  }

  componentDidUpdate(prevProps) {
    const {
      dispatch,
      shelf: {
        creator: { login },
      },
    } = this.props
    const prevLogin = prevProps.shelf.creator.login

    if (login !== prevLogin) {
      dispatch(loadShelves(login, 1, 4))
    }
  }

  loadPosts = () => {
    const {
      dispatch,
      params: { uuid },
      postsPage,
    } = this.props

    return dispatch(loadPosts({ uuid, p: postsPage, pp: 20, append: true }))
  }

  onAddClick = () => {
    const {
      dispatch,
      shelf: { uuid },
      app,
    } = this.props
    dispatch(push(urlFor.shelfChooseAllToAdd(uuid, app.storedQuery)))
  }

  getUserShelves() {
    const {
      shelf: { uuid },
      userShelves,
    } = this.props

    return userShelves.filter(shelf => shelf.uuid !== uuid).slice(0, 3)
  }

  render() {
    if (!this.props.shelf) return null

    const {
      app,
      shelf,
      shelf: { topics, annotation, followers_count, state },
      shelfFollowers,
      shelfPosts,
    } = this.props

    const isMobile = deviceHelper.isMobileSize(app)

    const inlineStyles = {}

    return (
      <div>
        {state === 'hidden' && <Meta name="robots" content="noindex" />}
        <HeaderBox />
        <ShelfInfo shelf={shelf} renderButton={this.renderActionButtons} />
        <ShelfSubHeaderBox />
        <div style={inlineStyles}>
          <LayoutColumn>
            <Spacer size={isMobile ? 16 : 40} />
            {isMobile && this.renderActionButtons()}
            <Spacer size={16} />
            <ShelfAnnotation annotation={annotation} />
            <Spacer size={24} />
            <UsersList
              query={app.storedQuery}
              users={shelfFollowers}
              usersCount={followers_count}
              message="shelf.followed"
            />
            <Spacer size={16} />
            <TopicList>
              {topics.map((topic, i) => (
                <Topic {...topic} resource="books" key={i} />
              ))}
            </TopicList>
            <Spacer size={24} />
            {this.renderBookCards()}
            <Spacer size={32} />
            {shelfPosts.length < shelf.posts_count && (
              <MoreButtonWithLoading load={this.loadPosts} centered />
            )}
            <Spacer size={32} />
            <PromoGifts query={app.storedQuery} />
          </LayoutColumn>
        </div>
        <Spacer size={24} />
        {this.renderMoreShelves()}
        <Footer />
      </div>
    )
  }

  renderBookCards() {
    const { shelfPosts } = this.props

    return (
      <List>
        {shelfPosts.map((post, index) => (
          <CardPostBox post={post} key={`${post.uuid}-${index}`} />
        ))}
      </List>
    )
  }

  renderMoreShelves() {
    const {
      i18n,
      currentUser,
      app,
      shelf: { creator },
    } = this.props

    const shelves = this.getUserShelves()

    if (!shelves.length) return null

    return (
      <Showcase>
        <MoreShelves
          shelves={shelves}
          header={i18n.t`shelf.more_shelves`}
          allLink={urlFor.userShelves(creator.login, app.storedQuery)}
          currentUserLogin={currentUser.data.login}
          query={app.storedQuery}
        />
        <Spacer size={32} />
      </Showcase>
    )
  }

  renderActionButtons = () => {
    const {
      app,
      shelf,
      currentUser: { auth, data },
      shelf: { following, editable, uuid },
    } = this.props

    const isMobileOS = deviceHelper.isMobileOS(app)
    const isMobileSize = deviceHelper.isMobileSize(app)

    return (
      <div>
        {isMobileOS && ![DK_SHELF_UUID, DK_SUMMER_SHELF_UUID].includes(uuid) && (
          <div>
            <ShelfDeeplinkButton shelf={shelf} />
            <Spacer size={isMobileSize ? 8 : 16} />
          </div>
        )}
        {editable && (
          <ButtonsRow>
            <Button
              kind="primary"
              type="inline"
              width="160px"
              onClick={this.onAddClick}
            >
              <Trans id="shelf.add_book" />
            </Button>
            <ShelfContextMenuBox shelf={shelf} kind="square" />
            {isShareAvailable(data.country) && (
              <ShareButton
                kind="medium"
                shareData={this.getShareData()}
                analytics={{
                  object_type: 'shelf',
                  object_id: uuid,
                }}
              />
            )}
          </ButtonsRow>
        )}
        {!editable &&
          (!isMobileOS ||
            (isMobileOS && auth) ||
            (isMobileOS &&
              [DK_SHELF_UUID, DK_SUMMER_SHELF_UUID].includes(uuid))) && (
            <ButtonsRow>
              <FollowButtonBox
                shelf={shelf}
                following={following}
                width="160px"
                className={!isMobileSize && following ? 'button_dark' : ''}
              />
              {isShareAvailable(data.country) && (
                <ShareButton
                  kind="medium"
                  shareData={this.getShareData()}
                  analytics={{
                    object_type: 'shelf',
                    object_id: uuid,
                  }}
                />
              )}
            </ButtonsRow>
          )}
      </div>
    )
  }

  renderAddButton = () => {
    return (
      <Button
        kind="primary"
        type="inline"
        width="100%"
        onClick={this.onAddClick}
      >
        <Trans id="shelf.add_book" />
      </Button>
    )
  }
}

const prepareWrapper = prepareComponent(
  async function ({ store, params }) {
    const { uuid } = params
    const { dispatch } = store
    const actions = [
      dispatch(loadPosts({ uuid, pp: 20, page: 1 })),
      dispatch(loadFollowers({ uuid, pp: 15, page: 1 })),
    ]
    await Promise.all(actions)
  },
  {
    updateParams: 'uuid',
    userListener: true,
  },
)

const connectWrapper = connect((state: State) => ({
  app: state.app,
  currentUser: state.currentUser,
  shelf: getShelfById(state, state.shelf.uuid),
  shelfFollowers: getUsersByIds(state, state.shelf.followers),
  shelfPosts: getPosts(state.shelf.posts, state.entities),
  userShelves: getShelvesByIds(state, state.user.shelves.created),
  postsPage: state.shelf.pages.posts,
}))

const wrappers = compose(
  prepareWrapper,
  connectWrapper,
  errorBoundary,
  withI18n({ update: true }),
  withMarketing,
)

export default wrappers(ShelfIndexBox)
